import {naturalSort} from '@benchling/js-utils';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import {ProcessClientOrigin, ReviewerStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getDefaultSearchSearchParams} from 'nav/sidepanel/listing/SearchSearchParams';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import * as SidepanelReduxActions from 'nav/sidepanel/SidepanelReduxActions';
import ReviewConstants from 'notebook/reviews/ReviewConstants';
import {
  isDashboardTypeAssignable,
  SUPPORTED_DASHBOARD_TYPES_BY_PROCESS_CLIENT_ORIGIN,
} from 'processes/dashboard/ProcessManagementSidepanelConstants';
import {getEnabledClientOrigins} from 'processes/ProcessConstants';
import {getStore} from 'redux-stores/Store';
import {Filter, FilterKey, keyToEmptyFilter, Operator} from 'search/SearchConstants';
import * as SearchUtil from 'search/SearchUtil';
import isModelTypeEnabledForListing from 'util/isModelTypeEnabledForListing';
import {assertNever} from 'util/Types';

import {ReviewItemStatusLabel} from './MyWorkConstants';

interface ReviewSearchFilter {
  review_status: string | null;
  reviewerStatus: ReviewerStatus | null;
  role: string;
}

function getAuthorRoleFilter(): string {
  return BenchlingConfig.ENABLE_CONSISTENT_REVIEW_SUMMARY_COUNT
    ? ReviewConstants.ROLE_FILTERS.AUTHOR
    : ReviewConstants.ROLE_FILTERS.AUTHOR_OR_AUDITOR;
}

function getReviewStatusSearchFilter(statusLabel: ReviewItemStatusLabel): ReviewSearchFilter {
  switch (statusLabel) {
    case 'in progress':
      return {
        review_status: ReviewConstants.IN_PROGRESS_FILTER,
        reviewerStatus: null,
        role: getAuthorRoleFilter(),
      };
    case 'needs revision':
      return {
        review_status: ReviewConstants.REJECTED,
        reviewerStatus: null,
        role: getAuthorRoleFilter(),
      };
    case 'ready for review':
      return {
        review_status: ReviewConstants.NEEDS_REVIEW,
        reviewerStatus: ReviewerStatus.PENDING,
        role: ReviewConstants.ROLE_FILTERS.AUDITOR,
      };
    case 'sent for review':
      return {
        review_status: ReviewConstants.NEEDS_REVIEW,
        reviewerStatus: null,
        role: ReviewConstants.ROLE_FILTERS.AUTHOR,
      };
    case 'completed':
      return {
        review_status: ReviewConstants.ACCEPTED,
        reviewerStatus: null,
        role: ReviewConstants.ROLE_FILTERS.AUTHOR_OR_AUDITOR,
      };
    default:
      return assertNever(statusLabel);
  }
}

export function openMyWorkReviewableStatusSearch(
  modelTypes: Array<ModelTypeConstants>,
  statusLabel: ReviewItemStatusLabel
): void {
  getStore().dispatch(
    SidepanelReduxActions.openListing(
      NavConstants.Section.SEARCH,
      SearchUtil.mergeFiltersToSearchParams(getDefaultSearchSearchParams(), [
        {
          ...keyToEmptyFilter(FilterKey.TYPES),
          value: modelTypes.filter((type) => isModelTypeEnabledForListing(type)),
        },
        {
          ...keyToEmptyFilter(FilterKey.REVIEW),
          value: getReviewStatusSearchFilter(statusLabel),
        },
      ])
    )
  );
  SidepanelActions.expand();
}

/** Opens search or the processes dashboard for tasks assigned to the current user. */
export function openAssignedTasksStatusSearch(statusIds: Array<string> | null): void {
  const enabledClientOrigins = getEnabledClientOrigins();

  const searchFilterTypes: Array<ModelTypeConstants> = [];
  for (const clientOrigin of enabledClientOrigins) {
    const supportedTypes = SUPPORTED_DASHBOARD_TYPES_BY_PROCESS_CLIENT_ORIGIN[clientOrigin];
    for (const supportedType of supportedTypes) {
      if (isDashboardTypeAssignable(supportedType)) {
        searchFilterTypes.push(supportedType);
      }
    }
  }

  const searchFilters: Array<Filter> = [
    {
      ...keyToEmptyFilter(FilterKey.TYPES),
      value: naturalSort(_.unique(searchFilterTypes)),
    },
    {
      ...keyToEmptyFilter(FilterKey.PROCESS_TASK_ASSIGNEE),
      value: null,
      operator: Operator.CURRENT_USER,
    },
  ];
  if (statusIds != null && statusIds.length > 0) {
    searchFilters.push({
      ...keyToEmptyFilter(FilterKey.PROCESS_TASK_STATUS_FOR_ALL_TASK_TYPES),
      value: statusIds,
    });
  }

  const navSectionToShow =
    enabledClientOrigins.length === 1 && enabledClientOrigins[0] === ProcessClientOrigin.PROCESSES
      ? NavConstants.Section.PROCESSES
      : NavConstants.Section.SEARCH;

  if (navSectionToShow === NavConstants.Section.PROCESSES) {
    searchFilters.push({
      ...keyToEmptyFilter(FilterKey.PROCESS_CLIENT_ORIGIN),
      value: ProcessClientOrigin.PROCESSES,
    });
  }

  getStore().dispatch(
    SidepanelReduxActions.openListing(
      navSectionToShow,
      SearchUtil.mergeFiltersToSearchParams(getDefaultSearchSearchParams(), searchFilters)
    )
  );
  SidepanelActions.expand();
}
